'use client'

import { useState } from 'react'
import Image from '@/components/Image'
import Link from 'next/link'
import { Trash2, Plus, Minus } from 'lucide-react'
import { Button } from '@/components/Button'
import { useCartStore } from '@/stores/cartStore'
import type { CartItem as CartItemType } from '@/stores/cartStore'
import formatCurrency from '@/lib/common/utils/formatCurrency'

interface CartItemProps {
  item: CartItemType
  showRemoveButton?: boolean
  showQuantityControls?: boolean
}

export function CartItem({ 
  item, 
  showRemoveButton = true, 
  showQuantityControls = true 
}: CartItemProps) {
  const { updateQuantity, removeItem } = useCartStore()
  const [isUpdating, setIsUpdating] = useState(false)

  const { product, quantity } = item
  const maxAllowed = product.maxPerUser || Infinity
  const canIncrease = quantity < maxAllowed
  const canDecrease = quantity > 1

  const handleQuantityChange = async (newQuantity: number) => {
    setIsUpdating(true)
    updateQuantity(product.id, newQuantity)
    
    // Small delay to show updating state
    setTimeout(() => {
      setIsUpdating(false)
    }, 200)
  }

  const handleRemove = () => {
    removeItem(product.id)
  }

  const itemTotal = product.price * quantity
  const hasDiscount = product.compareAtPrice && product.compareAtPrice > product.price

  return (
    <div className="flex gap-4 p-4 bg-white rounded-lg border border-slate-200">
      {/* Product Image */}
      <div className="flex-shrink-0">
        <Link href={`/producten/${product.slug}`} className="block">
          <div className="w-16 h-16 bg-slate-100 rounded-lg overflow-hidden">
            {product.featuredImage && typeof product.featuredImage === 'object' && product.featuredImage.url ? (
              <Image
                src={product.featuredImage.url}
                alt={product.featuredImage.alt || product.name}
                width={64}
                height={64}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-400">
                <span className="text-2xl">📦</span>
              </div>
            )}
          </div>
        </Link>
      </div>

      {/* Product Details */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1 min-w-0 pr-2">
            <Link 
              href={`/producten/${product.slug}`}
              className="font-medium text-slate-900 hover:text-ocean-blue transition-colors line-clamp-2"
            >
              {product.name}
            </Link>
            
            {/* Product Type */}
            <div className="text-xs text-slate-500 mt-1">
              {getProductTypeLabel(product.productType)}
            </div>
          </div>

          {/* Remove Button */}
          {showRemoveButton && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRemove}
              className="text-slate-400 hover:text-red-500 flex-shrink-0"
              title="Verwijder uit winkelwagen"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Quantity and Price Row */}
        <div className="flex items-center justify-between">
          {/* Quantity Controls */}
          {showQuantityControls ? (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => handleQuantityChange(quantity - 1)}
                disabled={!canDecrease || isUpdating}
                className="w-8 h-8"
              >
                <Minus className="w-3 h-3" />
              </Button>
              
              <span className="w-8 text-center text-sm font-medium">
                {isUpdating ? '...' : quantity}
              </span>
              
              <Button
                variant="outline"
                size="icon"
                onClick={() => handleQuantityChange(quantity + 1)}
                disabled={!canIncrease || isUpdating}
                className="w-8 h-8"
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            <div className="text-sm text-slate-600">
              Aantal: {quantity}
            </div>
          )}

          {/* Price */}
          <div className="text-right">
            <div className="font-semibold text-slate-900">
              {formatCurrency(itemTotal)}
            </div>
            {hasDiscount && (
              <div className="text-xs text-slate-500 line-through">
                {formatCurrency(product.compareAtPrice! * quantity)}
              </div>
            )}
            {quantity > 1 && (
              <div className="text-xs text-slate-500">
                {formatCurrency(product.price)} per stuk
              </div>
            )}
          </div>
        </div>

        {/* Max quantity warning */}
        {product.maxPerUser && quantity >= product.maxPerUser && (
          <div className="text-xs text-amber-600 mt-2">
            Maximum bereikt ({product.maxPerUser} per bestelling)
          </div>
        )}
      </div>
    </div>
  )
}

function getProductTypeLabel(type: string): string {
  const labels = {
    physical: 'Fysiek product',
    digital: 'Digitaal product', 
    ticket: 'Ticket',
    service: 'Service',
    subscription: 'Abonnement',
  }
  return labels[type as keyof typeof labels] || type
}